import React, { useState, useEffect, useRef, useCallback } from 'react';
import './ExamMode.css';
import { Timer } from './Timer';
import { QuestionBlock } from './QuestionBlock';
import type { QuestionConfig, ResponseType } from './QuestionBlock';

// ── Types ──────────────────────────────────────────────────────────

export type ExamSection = {
  id: string;
  title: string;
  instructions: string;
  recommendedMinutes: number;
  questions: QuestionConfig[];
};

export type ExamModeConfig = {
  exam: 'HSC' | 'VCE';
  subject: string;
  year: number;
  totalDurationMinutes: number;
  readingTimeMinutes?: number;
  sections: ExamSection[];
};

interface Props {
  config: ExamModeConfig;
  lessonId: string;
  widgetId: string;
}

// ── Helpers ────────────────────────────────────────────────────────

function countWords(text: string): number {
  return text.trim() === '' ? 0 : text.trim().split(/\s+/).length;
}

function fmtDuration(totalSeconds: number): string {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function emitLumi(lessonId: string, widgetId: string, type: string, payload: Record<string, unknown>) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__lumiObserver__?.emit({ lessonId, widgetId, type, payload });
  } catch { /* degrade silently */ }
}

// ── Main component ─────────────────────────────────────────────────

export function ExamModeWidget({ config, lessonId, widgetId }: Props) {
  if (!config || !config.sections) {
    throw new Error(`ExamModeWidget: config.sections is missing (exam=${config?.exam}, subject=${config?.subject})`);
  }
  const {
    exam, subject, year, sections,
  } = config;
  // Some overnight pipeline entries omit totalDurationMinutes — default to a generous 60 min
  const totalDurationMinutes = config.totalDurationMinutes ?? 60;
  const readingMinutes = config.readingTimeMinutes ?? 0;

  const totalSeconds   = totalDurationMinutes * 60;
  const readingSeconds = readingMinutes * 60;

  // ── Timer state ────────────────────────────────────────────────

  const [secondsLeft,        setSecondsLeft]        = useState(totalSeconds);
  const [readingSecondsLeft, setReadingSecondsLeft] = useState(readingSeconds);
  const [inReadingTime,      setInReadingTime]      = useState(readingSeconds > 0);
  const [timesUp,            setTimesUp]            = useState(false);

  // ── Exam state ─────────────────────────────────────────────────

  const [activeSection, setActiveSection] = useState(0);
  const [responses, setResponses] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    sections.flatMap(s => s.questions).forEach(q => { init[q.id] = ''; });
    return init;
  });
  const [submitted,   setSubmitted]   = useState(false);
  const [secondsAtSubmit, setSecondsAtSubmit] = useState<number | null>(null);
  const [lastSaved,   setLastSaved]   = useState<Date | null>(null);

  // ── Refs for stable access in intervals ────────────────────────

  const responsesRef     = useRef(responses);
  const submittedRef     = useRef(submitted);
  useEffect(() => { responsesRef.current = responses;  }, [responses]);
  useEffect(() => { submittedRef.current = submitted;  }, [submitted]);

  // ── Restore autosaved essay drafts on mount ────────────────────

  useEffect(() => {
    const restored: Record<string, string> = {};
    sections.flatMap(s => s.questions)
      .filter(q => q.responseType === 'essay')
      .forEach(q => {
        const saved = localStorage.getItem(`exammode_draft_${lessonId}_${q.id}`);
        if (saved) restored[q.id] = saved;
      });
    if (Object.keys(restored).length > 0) {
      setResponses(prev => ({ ...prev, ...restored }));
    }
    emitLumi(lessonId, widgetId, 'widget_mounted', {
      widgetType: 'ExamMode', exam, subject, year,
      totalDurationMinutes, sectionCount: sections.length,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Timer countdown ────────────────────────────────────────────

  useEffect(() => {
    if (submitted) return;

    let id: ReturnType<typeof setInterval>;

    if (inReadingTime) {
      id = setInterval(() => {
        setReadingSecondsLeft(prev => {
          if (prev <= 1) { setInReadingTime(false); return 0; }
          return prev - 1;
        });
      }, 1000);
    } else {
      id = setInterval(() => {
        setSecondsLeft(prev => {
          if (prev <= 1) { setTimesUp(true); return 0; }
          return prev - 1;
        });
      }, 1000);
    }

    return () => clearInterval(id);
  }, [submitted, inReadingTime]);

  // ── Autosave essay responses every 30s ─────────────────────────

  useEffect(() => {
    if (submitted) return;
    const essayIds = sections.flatMap(s => s.questions)
      .filter(q => q.responseType === 'essay')
      .map(q => q.id);
    if (essayIds.length === 0) return;

    const id = setInterval(() => {
      essayIds.forEach(qId => {
        localStorage.setItem(
          `exammode_draft_${lessonId}_${qId}`,
          responsesRef.current[qId] ?? '',
        );
      });
      setLastSaved(new Date());
    }, 30_000);

    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted, lessonId]);

  // ── Handlers ───────────────────────────────────────────────────

  const handleResponseChange = useCallback((questionId: string, value: string) => {
    setResponses(prev => ({ ...prev, [questionId]: value }));
  }, []);

  function handleSubmit() {
    const allQuestions = sections.flatMap(s => s.questions);
    const wordCounts   = Object.fromEntries(
      allQuestions.map(q => [q.id, countWords(responses[q.id] ?? '')])
    );
    const totalWords = Object.values(wordCounts).reduce((a, b) => a + b, 0);
    const timeUsed   = totalSeconds - secondsLeft;

    setSubmitted(true);
    setSecondsAtSubmit(secondsLeft);

    // Clear autosave on intentional submit
    allQuestions.filter(q => q.responseType === 'essay').forEach(q => {
      localStorage.removeItem(`exammode_draft_${lessonId}_${q.id}`);
    });

    emitLumi(lessonId, widgetId, 'exam_submitted', {
      timeUsedSeconds: timeUsed,
      totalWords,
      wordCounts,
      responses: Object.fromEntries(
        allQuestions.map(q => [q.id, responses[q.id] ?? ''])
      ),
    });
  }

  // ── Derived state ──────────────────────────────────────────────

  const allQuestions  = sections.flatMap(s => s.questions);
  const totalWords    = allQuestions.reduce((sum, q) => sum + countWords(responses[q.id] ?? ''), 0);
  const readOnly      = submitted || (timesUp && !submitted) || inReadingTime;
  const hasAnyResponse = allQuestions.some(q => (responses[q.id] ?? '').trim().length > 0);

  // ── Summary screen ─────────────────────────────────────────────

  if (submitted) {
    const timeUsedSecs = totalSeconds - (secondsAtSubmit ?? 0);
    return (
      <div className="em-root">
        {/* Header */}
        <div className="em-header">
          <span className={`em-header-exam-badge em-header-exam-badge--${exam}`}>{exam}</span>
          <span className="em-header-title">{subject}</span>
          <span className="em-header-year">{year}</span>
        </div>

        <div className="em-summary">
          <div className="em-summary-check">✅</div>
          <h2 className="em-summary-title">Paper Submitted</h2>
          <p className="em-summary-subject">{exam} {subject} — {year}</p>

          <div className="em-summary-stat-grid">
            <div className="em-summary-stat">
              <div className="em-summary-stat-value">{fmtDuration(timeUsedSecs)}</div>
              <div className="em-summary-stat-label">Time used</div>
            </div>
            <div className="em-summary-stat">
              <div className="em-summary-stat-value">{totalWords.toLocaleString()}</div>
              <div className="em-summary-stat-label">Words written</div>
            </div>
            <div className="em-summary-stat">
              <div className="em-summary-stat-value">{allQuestions.length}</div>
              <div className="em-summary-stat-label">Questions answered</div>
            </div>
            {secondsAtSubmit != null && secondsAtSubmit > 0 && (
              <div className="em-summary-stat">
                <div className="em-summary-stat-value">{fmtDuration(secondsAtSubmit)}</div>
                <div className="em-summary-stat-label">Time remaining</div>
              </div>
            )}
          </div>

          <hr className="em-summary-divider" />

          {/* Per-section review */}
          {sections.map(section => {
            let qNum = 0;
            return (
              <div key={section.id} style={{ marginBottom: 28 }}>
                <p className="em-summary-section-title">{section.title}</p>
                {section.questions.map(q => {
                  qNum++;
                  const wc = countWords(responses[q.id] ?? '');
                  return (
                    <QuestionBlock
                      key={q.id}
                      question={q}
                      questionNumber={qNum}
                      response={responses[q.id] ?? ''}
                      onChange={() => {}}
                      readOnly
                      showMarkingCriteria
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Active exam view ───────────────────────────────────────────

  const section = sections[activeSection];
  let questionOffset = sections
    .slice(0, activeSection)
    .reduce((sum, s) => sum + s.questions.length, 0);

  return (
    <div className="em-root">
      <span className="em-live" aria-live="polite" aria-atomic="true" />

      {/* Header */}
      <div className="em-header">
        <span className={`em-header-exam-badge em-header-exam-badge--${exam}`}>{exam}</span>
        <span className="em-header-title">{subject}</span>
        <span className="em-header-year">{year}</span>
        <Timer
          secondsLeft={secondsLeft}
          totalSeconds={totalSeconds}
          inReadingTime={inReadingTime}
          readingSecondsLeft={readingSecondsLeft}
          timesUp={timesUp}
        />
      </div>

      {/* Banners */}
      {inReadingTime && (
        <div className="em-banner em-banner--reading">
          📖 Reading time — {Math.ceil(readingSecondsLeft / 60)} min remaining. You may not write yet.
        </div>
      )}
      {timesUp && (
        <div className="em-banner em-banner--timesup">
          ⏰ Time's up — review your paper and submit when ready.
        </div>
      )}
      {lastSaved && !timesUp && !inReadingTime && (
        <div className="em-banner em-banner--autosave">
          💾 Draft autosaved at {lastSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        </div>
      )}

      {/* Section tabs (only show if multiple sections) */}
      {sections.length > 1 && (
        <div className="em-tabs" role="tablist">
          {sections.map((s, i) => (
            <button
              key={s.id}
              role="tab"
              aria-selected={activeSection === i}
              className={`em-tab${activeSection === i ? ' em-tab--active' : ''}`}
              onClick={() => setActiveSection(i)}
            >
              {s.title}
              <span className="em-tab-time">({s.recommendedMinutes} min)</span>
            </button>
          ))}
        </div>
      )}

      {/* Section body */}
      <div className="em-section-body">
        <div className="em-section-header">
          {sections.length === 1 && (
            <h2 className="em-section-title">{section.title}</h2>
          )}
          {section.instructions && (
            <p className="em-section-instructions">{section.instructions}</p>
          )}
          <p className="em-recommended-time">
            Recommended time: {section.recommendedMinutes} minutes
          </p>
        </div>

        {/* Questions */}
        {section.questions.map((q, qi) => (
          <QuestionBlock
            key={q.id}
            question={q}
            questionNumber={questionOffset + qi + 1}
            response={responses[q.id] ?? ''}
            onChange={handleResponseChange}
            readOnly={readOnly}
            showMarkingCriteria={false}
          />
        ))}
      </div>

      {/* Submit bar */}
      <div className="em-submit-bar">
        <span className="em-submit-progress">
          {totalWords > 0
            ? `${totalWords.toLocaleString()} word${totalWords !== 1 ? 's' : ''} written`
            : 'No responses yet'}
        </span>
        <button
          className="em-btn-submit-exam"
          onClick={handleSubmit}
        >
          Submit Paper
        </button>
      </div>
    </div>
  );
}
